import * as React from 'react';
import Grid from '@mui/material/Grid2';
import { observer } from 'mobx-react';
import BaseSelect from '../../../../components/BaseSelect';
import {
  LAUNCH_BUTTON,
  PREVIEW_BUTTON,
  SOCKET_USER_UPDATE_EVENT,
} from '../../../../constants/base';
import BaseInput from '../../../../components/BaseInput';
import BaseButton from '../../../../components/BaseButton';
import DataStore from '../../../../store';
import { validateForm } from '../../../../helpers/formValidator';
import { notificationStore } from '../../../../store/Notification';
import { MISSING_FIELDS_MESSAGE } from '../../../../helpers/constants';
import socketStore from '../../../../store/Socket';
import ProgressBar from '../../../../components/ProgressBar';
import HelperText from '../../../../components/HelperText';
import { validationRules } from '../../../../helpers/validationRules';
import { Box } from '@mui/material';
import { getPageConfiguration } from '../../../../helpers/groups';
import { useSocketListener } from '../../../../hooks/useSocketListener';
import { ProgressPayload } from '../../../../types/global';

const Standalone = observer(() => {
  const [progress, setProgress] = React.useState(0);
  const [progressMax, setProgressMax] = React.useState(0);
  const [userHelper, setUserHelper] = React.useState<{
    data: any[];
    message: string;
  }>({ data: [], message: '' });
  const [progressMessage, setProgressMessage] = React.useState('');
  const [errors, setErrors] = React.useState({});

  const resetProgressBar = () => {
    setProgress(0);
    setProgressMax(0);
    setProgressMessage('');
  };

  const fieldLabels: { [key: string]: string } = {
    inputPath: 'Input Path',
    outputPrefix: 'Output Prefix',
    sampleId: 'Sample ID',
    includeKeywords: 'Include Keywords',
    excludeKeywords: 'Exclude Keywords',
  };

  React.useEffect(() => {
    showOutput();
    setUserHelper({ data: [], message: '' });
  }, []);

  const socket = socketStore.socket;
  const channel = React.useMemo(() => SOCKET_USER_UPDATE_EVENT(), []);
  const onProgress = React.useCallback((data: ProgressPayload): void => {
    setProgress(data.progress ?? 0);
    setProgressMax(data.total ?? 0);
    setProgressMessage(data.message ?? '');
  }, []);
  useSocketListener<ProgressPayload>(socket, channel, onProgress, true);

  const showOutput = (resData: any = {}) => {
    DataStore.showOutput('Preview standalone', resData, 'html');
  };

  const getValidationSchema = (command: string) => {
    if (command === 'Delivery report') {
      return {
        inputPath: { ...validationRules.inputPath, required: true },
        outputPrefix: { ...validationRules.outputPrefix },
        sampleId: { ...validationRules.sampleId, required: true },
      };
    }
    return {
      inputPath: { ...validationRules.inputPath, required: true },
      outputPrefix: { ...validationRules.outputPrefix },
      includeKeywords: { ...validationRules.includeKeywords },
      excludeKeywords: { ...validationRules.excludeKeywords },
    };
  };

  const defaultData = {
    command: 'list',
    inputPath: '',
    outputPrefix: '',
    sampleId: '',
    configPath: '',
    includeKeywords: '',
    excludeKeywords: '',
  };

  const [data, setData] = React.useState({ ...defaultData });

  const previewRun = async (e: any) => {
    e.preventDefault();
    resetProgressBar();
    setUserHelper({ data: [], message: '' });

    const validationSchema = getValidationSchema(data.command);
    const validError = validateForm(data, validationSchema);
    if (!validError) {
      setErrors({});
      const resData: any = await DataStore.standalonePreviewRequest(data);
      if (resData?.messageType === 'error') {
        resetProgressBar();
        if (resData.help) {
          setUserHelper({
            data: resData.data ?? [],
            message:
              resData.message ||
              'Too many results detected, please update your filters accordingly.',
          });
        }
      } else if (resData) {
        showOutput(resData);
        DataStore.showOutput('Preview standalone', resData, 'html');
        setProgressMessage('Ready.');
        setProgress(100);
      }
    } else {
      resetProgressBar();
      const isRequiredMissing = (Object.values(validError) as string[]).some(
        (msg) => msg.toLowerCase().includes('is required')
      );

      const humanizedErrors = Object.entries(validError).reduce(
        (acc: any, [key, message]) => {
          const label = fieldLabels[key] || key;
          const safeMessage =
            typeof message === 'string' ? message : String(message);
          acc[label] = safeMessage.replace(new RegExp(key, 'gi'), label);
          return acc;
        },
        {}
      );
      notificationStore.showMessage(
        isRequiredMissing ? MISSING_FIELDS_MESSAGE : 'Some fields are invalid.',
        'error',
        humanizedErrors
      );
      setErrors(humanizedErrors);
    }
  };

  const launchRun = async () => {
    resetProgressBar();
    setUserHelper({ data: [], message: '' });
    await DataStore.standaloneLaunchRequest();
    setProgressMessage('Finished.');
    setProgress(100);
  };

  const onChangeData = (event: any) => {
    const { name, value }: any = event.target;
    setData((prevData: any) => {
      if (name === 'command') {
        setUserHelper({ data: [], message: '' });
        resetProgressBar();
        DataStore.showOutput('Preview standalone', {}, 'html');
        DataStore.setStandalonePreview(false);
        return { ...defaultData, command: value };
      }
      return { ...prevData, [name]: value };
    });
  };

  const renderCommandForm = () => {
    switch (data.command) {
      case 'Merge wells':
      case 'Merge slides':
        return (
          <>
            <Grid size={12}>
              <h1>{data.command}</h1>
            </Grid>
            <Grid size={{ xs: 6, md: 8 }}>
              <BaseInput
                feature={'standalone_mergeWells'}
                label={fieldLabels.inputPath}
                value={data.inputPath}
                name='inputPath'
                onChange={onChangeData}
                errors={errors}
                required
              />
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <BaseInput
                feature={'standalone_mergeWells'}
                label={fieldLabels.outputPrefix}
                value={data.outputPrefix}
                name='outputPrefix'
                onChange={onChangeData}
                errors={errors}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 6 }}>
              <BaseInput
                feature={'standalone_mergeWells'}
                label={fieldLabels.includeKeywords}
                value={data.includeKeywords}
                name='includeKeywords'
                onChange={onChangeData}
                errors={errors}
                description='Comma separated'
              />
            </Grid>
            <Grid size={{ xs: 6, md: 6 }}>
              <BaseInput
                feature={'standalone_mergeWells'}
                label={fieldLabels.excludeKeywords}
                value={data.excludeKeywords}
                name='excludeKeywords'
                onChange={onChangeData}
                errors={errors}
                description='Comma separated'
              />
            </Grid>
          </>
        );
      case 'Delivery report':
        return (
          <>
            <Grid size={12}>
              <h1>Delivery report</h1>
            </Grid>
            <Grid size={{ xs: 6, md: 12 }}>
              <BaseInput
                feature={'standalone_deliveryReport'}
                label={fieldLabels.inputPath}
                value={data.inputPath}
                name='inputPath'
                onChange={onChangeData}
                errors={errors}
                required
              />
            </Grid>
            <Grid size={{ xs: 6, md: 8 }}>
              <BaseInput
                feature={'standalone_deliveryReport'}
                label={fieldLabels.outputPrefix}
                value={data.outputPrefix}
                name='outputPrefix'
                onChange={onChangeData}
                errors={errors}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <BaseInput
                feature={'standalone_deliveryReport'}
                label={fieldLabels.sampleId}
                value={data.sampleId}
                name='sampleId'
                onChange={onChangeData}
                errors={errors}
                required
              />
            </Grid>
          </>
        );
      default:
        return <div>No command selected</div>;
    }
  };

  const getCommandValues = () => {
    const pageConfiguration: any = getPageConfiguration('standalone');
    return pageConfiguration?.command || [];
  };
  return (
    <>
      <Grid container spacing={2}>
        <Grid size={12}>
          <BaseSelect
            feature={'standalone'}
            name='command'
            label='Command'
            values={getCommandValues()}
            value={data.command}
            onChange={onChangeData}
          />
        </Grid>
        {renderCommandForm()}
        <Grid size={{ xs: 12, md: 12 }}>
          <Box display='flex' alignItems='center' gap={1}>
            <BaseButton
              text={PREVIEW_BUTTON}
              variant='outlined'
              color='primary'
              onClick={previewRun}
              isDisabled={DataStore.isProcessing}
              spinner={DataStore.isProcessing}
              customStyle={{ marginRight: '10px' }}
            />
            <BaseButton
              text={LAUNCH_BUTTON}
              variant='contained'
              color='primary'
              onClick={launchRun}
              isDisabled={
                DataStore.isProcessing || !DataStore.isStandalonePreview
              }
              spinner={DataStore.isProcessing}
            />
          </Box>
        </Grid>
        <Grid size={12}>
          <ProgressBar
            actual={progress}
            max={progressMax}
            message={progressMessage}
          />
          <HelperText
            data={userHelper.data}
            customMessage={userHelper.message}
          />
        </Grid>
      </Grid>
    </>
  );
});

export default Standalone;
